/**
 * SelectedViews.jsx
 * 
 * Renders a list of draggable project views.
 * Highlights the selected view and allows reordering via drag-and-drop.
 */

import PropTypes from "prop-types";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import List from "@mui/material/List";
import EditViewItem, { ViewItemPropType } from "./EditViewItem";

/**
 * List of project views with selection and reordering support.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onMove - Callback fired when a view is reordered via drag-and-drop.
 * @param {Function} props.onSelect - Callback fired when a view is selected.
 * @param {Array} props.projectViews - Array of project view objects to render.
 * @param {number|string} props.selectedViewId - ID of the currently selected view.
 */
const SelectedViews = ({
  onMove,
  onSelect,
  projectViews,
  selectedViewId,
}) => {
  if (!projectViews?.length) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      <List>
        {projectViews.map((view, index) => (
          <EditViewItem
            key={view.viewId}
            index={index}
            item={view}
            isSelected={view.viewId === selectedViewId}
            onMove={onMove}
            onSelect={() => onSelect(view.viewId)}
          />
        ))}
      </List>
    </DndProvider>
  );
};

SelectedViews.propTypes = {
  onMove: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  projectViews: PropTypes.arrayOf(ViewItemPropType).isRequired,
  selectedViewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SelectedViews.displayName = "SelectedViews";

export default SelectedViews;
